import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Search, X, Building2, Star } from 'lucide-react';
import apiClient from '../services/api';
import { SessionStatusBadge, ForceLogoutButton } from './ForceLogoutButton'; 

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const UserCompanyAccess = () => {
  const [accessList,         setAccessList]         = useState([]);
  const [users,              setUsers]              = useState([]);
  const [companies,          setCompanies]          = useState([]);
  const [filteredAccessList, setFilteredAccessList] = useState([]);
  const [loading,            setLoading]            = useState(true);
  const [searchTerm,         setSearchTerm]         = useState('');
  const [filterUserId,       setFilterUserId]       = useState('');
  const [showModal,          setShowModal]          = useState(false);
  const [editMode,           setEditMode]           = useState(false);
  const [submitLoading,      setSubmitLoading]      = useState(false);
  const [errors,             setErrors]             = useState({});

  const [formData, setFormData] = useState({ accessid: null, userid: '', companyid: '', isdefault: false });

  useEffect(() => { fetchAccessList(); fetchUsers(); fetchCompanies(); }, []);
  useEffect(() => { filterAccess(); }, [searchTerm, filterUserId, accessList]);

  const fetchAccessList = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/UserCompanyAccess.php');
      if (res.data.success) setAccessList(res.data.data || []);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
    } finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiClient.get('/Users.php', { params: { active: true } });
      if (res.data.success) setUsers(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchCompanies = async () => {
    try {
      const res = await apiClient.get('/Companies.php', { params: { active: true } });
      if (res.data.success) setCompanies(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const filterAccess = () => {
    let f = accessList;
    if (filterUserId) f = f.filter(a => a.userid === parseInt(filterUserId));
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      f = f.filter(a =>
        a.username.toLowerCase().includes(q) ||
        a.fullname.toLowerCase().includes(q) ||
        a.companyname.toLowerCase().includes(q) ||
        a.companycode.toLowerCase().includes(q)
      );
    }
    setFilteredAccessList(f);
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.userid)   errs.userid   = 'User is required';
    if (!formData.companyid) errs.companyid = 'Company is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const res = await apiClient[method]('/UserCompanyAccess.php', { ...formData, userid: parseInt(formData.userid), companyid: parseInt(formData.companyid), isdefault: formData.isdefault ? 1 : 0 });
      if (res.data.success) {
        alert(editMode ? 'Company access updated successfully!' : 'Company access granted successfully!');
        closeModal(); fetchAccessList();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else if (err.response?.status === 409) alert('User already has access to this company.');
      else alert('Error saving company access. Please try again.');
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (access) => {
    setFormData({ accessid: access.accessid, userid: access.userid, companyid: access.companyid, isdefault: access.isdefault === 1 });
    setEditMode(true); setShowModal(true); setErrors({});
  };
 
     
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this company access?')) return;
    try {
      const res = await apiClient.delete('/UserCompanyAccess.php', { data: { accessid: id } });
      if (res.data.success) { alert('Company access removed successfully!'); fetchAccessList(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error removing company access. Please try again.');
    }
  };

  const handleSetDefault = async (access) => {
    if (access.isdefault === 1) return;
    if (!window.confirm(`Set ${access.companyname} as default company for ${access.fullname}?`)) return;
    try {
      const res = await apiClient.put('/UserCompanyAccess.php', { accessid: access.accessid, isdefault: 1 });
      if (res.data.success) { alert('Default company updated successfully!'); fetchAccessList(); }
      else alert('Error: ' + res.data.message);
    } catch { alert('Error updating default company. Please try again.'); }
  };

  const resetForm    = () => { setFormData({ accessid: null, userid: '', companyid: '', isdefault: false }); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const selectCls = (err, disabled) =>
    `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)]
    ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-100' : 'cursor-pointer'}
    ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;

  const hasFilters = searchTerm || filterUserId;

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Building2 size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>User Company Access</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage which companies users can access</p>
          </div>
        </div>
        <button onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Grant Access
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by user or company name..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>

        <select value={filterUserId} onChange={e => setFilterUserId(e.target.value)}
          className="px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-700 bg-white outline-none cursor-pointer transition-all duration-150 hover:border-[#0d1c6b] focus:border-[#0d1c6b]">
          <option value="">All Users</option>
          {users.map(u => (
            <option key={u.userid} value={u.userid}>{u.username} — {u.fullname}</option>
          ))}
        </select>

        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredAccessList.length} {filteredAccessList.length === 1 ? 'access' : 'accesses'}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading company access...</div>
        ) : filteredAccessList.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <Building2 size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No company access records found</p>
            {hasFilters && (
              <button onClick={() => { setSearchTerm(''); setFilterUserId(''); }}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Clear filters</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['User', 'Full Name', 'Company', 'Default', 'Session', 'Granted At', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAccessList.map(access => (
                  <tr key={access.accessid}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5 font-semibold text-gray-800">{access.username}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-600">{access.fullname}</td>
                    <td className="px-4 py-3.5">
                      <span className="inline-block px-2.5 py-1 bg-blue-50 text-blue-700 border-2 border-blue-200 rounded-lg text-[12px] font-bold">
                        {access.companycode} — {access.companyname}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      {access.isdefault === 1 ? (
                        <Star size={18} className="mx-auto text-amber-400 fill-amber-400" title="Default Company" />
                      ) : (
                        <button onClick={() => handleSetDefault(access)} title="Set as default"
                          className="p-1 bg-transparent border-0 cursor-pointer rounded hover:bg-gray-100 transition-colors duration-150 mx-auto block">
                          <Star size={18} className="text-gray-300 hover:text-amber-300 transition-colors duration-150" />
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <SessionStatusBadge row={access} />
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">
                      {new Date(access.createdat).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <ForceLogoutButton row={access} onDone={fetchAccessList} />
                        <button onClick={() => handleDelete(access.accessid)} title="Remove Access"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[520px] shadow-2xl border border-gray-200 overflow-hidden"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <Building2 size={18} /> {editMode ? 'Edit Company Access' : 'Grant Company Access'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-7">

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    User <span className="text-red-500">*</span>
                  </label>
                  <select value={formData.userid} onChange={e => setFormData({ ...formData, userid: e.target.value })}
                    disabled={editMode} className={selectCls(errors.userid, editMode)}>
                    <option value="">-- Select User --</option>
                    {users.map(u => (
                      <option key={u.userid} value={u.userid}>{u.username} — {u.fullname}</option>
                    ))}
                  </select>
                  {errors.userid && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.userid}</p>}
                </div>
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    Company <span className="text-red-500">*</span>
                  </label>
                  <select value={formData.companyid} onChange={e => setFormData({ ...formData, companyid: e.target.value })}
                    disabled={editMode} className={selectCls(errors.companyid, editMode)}>
                    <option value="">-- Select Company --</option>
                    {companies.map(c => (
                      <option key={c.companyid} value={c.companyid}>{c.companycode} — {c.companyname}</option>
                    ))}
                  </select>
                  {errors.companyid && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.companyid}</p>}
                </div>
              </div>

              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                  <input type="checkbox" checked={formData.isdefault}
                    onChange={e => setFormData({ ...formData, isdefault: e.target.checked })}
                    className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <Star size={15} className="text-amber-400 fill-amber-400" />
                    Set as Default Company
                  </span>
                </label>
                <p className="text-[11px] text-gray-400 mt-1.5 pl-1">This company will be selected automatically on login</p>
              </div>

              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Grant Access')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default UserCompanyAccess;